import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useEnhancedAuth } from '../hooks/useEnhancedAuth';
import { usePWA } from '../hooks/usePWA';
import { toast } from 'react-hot-toast';
import PublicHelpTutorial from '../components/PublicHelpTutorial';

export default function EnhancedLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const postLoginDestination = redirectParam?.startsWith('/dashboard')
    ? redirectParam
    : '/dashboard';
  const { 
    enhancedLogin, 
    enhancedGoogleSsoLogin,
    quickLogin, 
    canUseQuickLogin,
    lastLoginEmail,
    error: authError 
  } = useEnhancedAuth();
  
  const { isMobile, isInstalled, isIOS } = usePWA();
  
  const [formData, setFormData] = useState({
    email: lastLoginEmail || '',
    password: '',
    // Consolidated option - different meaning based on device type
    rememberOption: false
  });
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showQuickLogin, setShowQuickLogin] = useState(false);
  const [ssoNotification, setSsoNotification] = useState('');

  const lastAuthMethod = localStorage.getItem('last-auth-method');
  const [showPasswordForm, setShowPasswordForm] = useState(lastAuthMethod !== 'google_sso');

  useEffect(() => {
    if (canUseQuickLogin && !showQuickLogin) {
      setShowQuickLogin(true);
    }
  }, [canUseQuickLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Add timeout protection
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setError('Login request timed out. Please try again.');
    }, 10000);

    try {
      // Consolidated option: 
      // - For desktop users: rememberOption = "Keep me signed in" (session persistence)
      // - For mobile/PWA users: rememberOption = "Remember me on this device" (session + device features)
      const result = await enhancedLogin(formData.email, formData.password, {
        rememberMe: formData.rememberOption,
        rememberDevice: formData.rememberOption // Same option enables device memory for mobile users
      });

      clearTimeout(timeoutId);

      if (result.success) {
        localStorage.setItem('last-auth-method', 'password');
        if (result.ssoNotification) {
          setSsoNotification(result.ssoNotification);
          toast(result.ssoNotification, { icon: 'ℹ️', duration: 6000 });
        } else {
          toast.success('Login successful!');
        }
        if (result.user && result.user.isFirstLogin) {
          navigate('/dashboard/user-settings', { 
            replace: true,
            state: { 
              isFirstLogin: true,
              message: 'Please complete your profile to continue' 
            }
          });
        } else {
          navigate(postLoginDestination, { replace: true });
        }
      } else {
        setError(result.error || 'Login failed');
        toast.error(result.error || 'Login failed');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      clearTimeout(timeoutId);
    }
  };

  const handleQuickLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await quickLogin();
      
      if (result.success) {
        const method = result.method === 'biometric' ? 
          'biometric authentication' : 
          'saved session';
        toast.success(`Logged in with ${method}!`);
        
        // Check if user is first-time login and redirect accordingly
        if (result.user && result.user.isFirstLogin) {
          navigate('/dashboard/user-settings', { 
            replace: true,
            state: { 
              isFirstLogin: true,
              message: 'Please complete your profile to continue' 
            }
          });
        } else {
          navigate(postLoginDestination, { replace: true });
        }
      } else {
        setError(result.error || 'Quick login failed');
        setShowQuickLogin(false);
      }
    } catch (err) {
      setError(err.message || 'Quick login failed');
      setShowQuickLogin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleGoogleSsoSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setError('Google sign-in failed. No credential received.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSsoNotification('');
    try {
      const result = await enhancedGoogleSsoLogin(credentialResponse.credential);
      if (result.success) {
        localStorage.setItem('last-auth-method', 'google_sso');
        toast.success('Signed in with Google!');
        if (result.user && (result.isFirstLogin || result.user.isFirstLogin)) {
          navigate('/dashboard/user-settings', {
            replace: true,
            state: {
              isFirstLogin: true,
              usedGoogleSso: true,
              message: 'Please complete your profile to continue. Since you signed in with Google, no password reset is needed.'
            }
          });
        } else {
          navigate(postLoginDestination, { replace: true });
        }
      } else {
        setError(result.error || 'Google sign-in failed');
        toast.error(result.error || 'Google sign-in failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during Google sign-in');
      toast.error(err.message || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSsoError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sky-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src="/images/logo.png"
            alt="Sweetgrass Properties"
          />
        </div>

        {/* Quick Login Options */}
        {showQuickLogin && (
          <div className="bg-sky-900/50 border border-sky-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-sky-200 mb-3">Quick Login</h3>
            <button
              onClick={handleQuickLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 bg-sky-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Continue Previous Session</span>
            </button>
            <button
              onClick={() => setShowQuickLogin(false)}
              className="mt-2 w-full text-xs text-sky-300 hover:text-sky-200 transition-colors duration-200"
            >
              Use regular login instead
            </button>
          </div>
        )}

        {/* Google-only view when last login was SSO */}
        {!showPasswordForm ? (
          <div className="space-y-6">
            {/* SSO reminder banner */}
            <div className="rounded-lg bg-sky-900/50 border border-sky-600 p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-sky-400 mt-0.5 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <p className="text-sm font-medium text-sky-200">Your last sign-in used Google Authentication</p>
                  <p className="text-xs text-sky-300/70 mt-1">Use the button below to continue with Google, or switch to password login.</p>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {(error || authError) && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 whitespace-pre-line">{error || authError}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* Google SSO Button - prominent */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSsoSuccess}
                onError={handleGoogleSsoError}
                theme="filled_black"
                size="large"
                width="400"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>

            {/* Switch to password login */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-sky-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-sky-950 text-sky-400">or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowPasswordForm(true)}
              className="w-full flex justify-center py-3 px-6 border border-sky-600 text-sm font-medium rounded-lg text-sky-300 hover:text-white hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sky-950 focus:ring-sky-400 transition-colors duration-200"
            >
              Switch to password login
            </button>
          </div>
        ) : (
          <>
            {/* Regular Login Form */}
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="sr-only">Email address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none relative block w-full px-4 py-4 text-base border border-sky-700 bg-sky-900/50 placeholder-sky-300/50 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 focus:z-10 sm:text-lg"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="relative">
                  <label htmlFor="password" className="sr-only">Password</label>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="appearance-none relative block w-full px-4 py-4 pr-12 text-base border border-sky-700 bg-sky-900/50 placeholder-sky-300/50 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 focus:z-10 sm:text-lg"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5 text-sky-300 hover:text-sky-200 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-sky-300 hover:text-sky-200 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Enhanced Options for Mobile/PWA */}
              {(isMobile || isInstalled) ? (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input id="remember-mobile" name="rememberOption" type="checkbox" className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-sky-600 rounded" checked={formData.rememberOption} onChange={handleInputChange} />
                    <label htmlFor="remember-mobile" className="ml-2 block text-sm text-sky-300">Remember me on this device</label>
                  </div>
                  <div className="text-sm">
                    <Link to="/forgot-password" className="text-sky-300 hover:text-sky-200 transition-colors duration-200">Forgot your password?</Link>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input id="remember-desktop" name="rememberOption" type="checkbox" className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-sky-600 rounded" checked={formData.rememberOption} onChange={handleInputChange} />
                    <label htmlFor="remember-desktop" className="ml-2 block text-sm text-sky-300">Keep me signed in</label>
                  </div>
                  <div className="text-sm">
                    <Link to="/forgot-password" className="text-sky-300 hover:text-sky-200 transition-colors duration-200">Forgot your password?</Link>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {(error || authError) && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 whitespace-pre-line">{error || authError}</h3>
                    </div>
                  </div>
                </div>
              )}

              {/* SSO Notification Banner */}
              {ssoNotification && (
                <div className="rounded-md bg-sky-900/50 border border-sky-600 p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-sky-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-sky-200">{ssoNotification}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-lg font-medium rounded-lg text-white bg-sky-600 hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sky-950 focus:ring-sky-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 min-h-[56px]"
                >
                  {isLoading ? (
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  ) : null}
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-sky-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-sky-950 text-sky-400">or</span>
              </div>
            </div>

            {/* Google SSO Button */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSsoSuccess}
                onError={handleGoogleSsoError}
                theme="filled_black"
                size="large"
                width="400"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>
          </>
        )}

        {/* First-time user hint */}
        <p className="text-center text-xs text-sky-400/70">
          New team member? You can sign in with your Google account if your email has been added to the system, or use the temporary password from your invitation.
        </p>

        {/* Help Tutorial */}
        <div className="mt-2 text-center">
          <PublicHelpTutorial 
            tutorialId={1} 
            buttonLabel="Login & Complete Profile Tutorial"
          />
        </div>
      </div>
    </div>
  );
}
